import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Eyebrow } from './SectionBand';
import { salesEnablementSlides, type SalesEnablementSlide } from '../../lib/salesEnablementSlides';
function Controls({
  index,
  total,
  onPrev,
  onNext
}: {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return <div className="flex items-center justify-center gap-4" data-id="element-992" data-mp="26hjd1m5d8epd">
      <button type="button" onClick={onPrev} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 bg-white text-navy transition-colors hover:border-navy/30 hover:bg-paper-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" aria-label="Previous slide" data-id="element-993" data-mp="21fu0u36epuzp">
        <ChevronLeft className="h-5 w-5" aria-hidden="true" data-id="element-994" data-mp="m04zts9yumsd" />
      </button>
      <p className="min-w-[3.5rem] text-center font-serif text-sm tabular-nums text-navy/70" aria-hidden="true" data-id="element-995" data-mp="h23gd68molgd">
        {index + 1} / {total}
      </p>
      <button type="button" onClick={onNext} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 bg-white text-navy transition-colors hover:border-navy/30 hover:bg-paper-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" aria-label="Next slide" data-id="element-996" data-mp="1r5cf9gfbqr0f">
        <ChevronRight className="h-5 w-5" aria-hidden="true" data-id="element-997" data-mp="25uijjit0r7f6" />
      </button>
    </div>;
}
function AssetImage({
  slide,
  priority,
  className = ''
}: {
  slide: SalesEnablementSlide;
  priority?: boolean;
  className?: string;
}) {
  return <img src={slide.src} alt={`${slide.label}: ${slide.outcome}`} width={1200} height={1200} loading={priority ? 'eager' : 'lazy'} decoding="async" draggable={false} className={`mx-auto aspect-square rounded-lg object-cover shadow-[0_12px_28px_-18px_rgba(26,40,56,0.35)] ${className}`} data-id="element-998" data-mp="2ew8a6mf2u2bw" />;
}

/** Caption lives under the active asset — title + benefit, not a second headline stack. */
function ActiveSlideCaption({
  slide,
  reduceMotion,
  transition
}: {
  slide: SalesEnablementSlide;
  reduceMotion: boolean | null;
  transition: {
    duration: number;
    ease?: readonly [number, number, number, number];
  };
}) {
  return <motion.div key={`copy-${slide.id}`} initial={reduceMotion ? false : {
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={transition} className="mx-auto max-w-xl text-center" data-id="element-999" data-mp="21phy8t770oe4">
      <p className="font-serif text-xl leading-snug text-navy md:text-2xl" data-id="element-1000" data-mp="26qp6etd8zplb">
        {slide.label}
      </p>
      <p className="mx-auto mt-1.5 text-pretty text-sm leading-relaxed text-text-muted md:text-base" data-id="element-1001" data-mp="2khuac7nnf41g">
        {slide.outcome}
      </p>
    </motion.div>;
}

/**
 * Asset-first carousel: short lead → stage → caption under active shot.
 */
export function SalesEnablementCarousel() {
  const slides = salesEnablementSlides;
  const [index, setIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const touchStartX = useRef<number | null>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const liveId = useId();
  const total = slides.length;
  const slide = slides[index];
  const prevSlide = slides[(index - 1 + total) % total];
  const nextSlide = slides[(index + 1) % total];
  const go = useCallback((next: number) => {
    setIndex((next % total + total) % total);
  }, [total]);
  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);
  useEffect(() => {
    const el = regionRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [prev, next]);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 48) return;
    if (dx > 0) prev();else next();
  };
  const transition = reduceMotion ? {
    duration: 0.12
  } : {
    duration: 0.32,
    ease: [0.22, 1, 0.36, 1] as const
  };
  return <div ref={regionRef} className="outline-none" role="region" aria-roledescription="carousel" aria-label="Business-building assets" tabIndex={0} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-1002" data-mp="1ljfv7k2ebi1a">
      <p id={liveId} className="sr-only" aria-live="polite" data-id="element-1003" data-mp="1bp60vwtw89nx">
        Slide {index + 1} of {total}: {slide.label}. {slide.outcome}
      </p>

      {/* Short lead — then stage */}
      <div className="mx-auto max-w-2xl text-center" data-id="element-1004" data-mp="360krsgyn4f59">
        <Eyebrow data-id="element-1005" data-mp="311rf2qr6gs65">Business-building assets</Eyebrow>
        <h2 className="mt-3 text-balance font-serif text-[1.65rem] leading-[1.14] tracking-[-0.01em] text-navy sm:text-3xl md:text-4xl" data-id="element-1006" data-mp="3132igpgpnxwq">
          <span className="block" data-id="element-1007" data-mp="3dkmw8dwqtewm">Give your book more&nbsp;ways</span>
          <span className="italic text-seaglass" data-id="element-1008" data-mp="3disflbsi88yc">to lead to revenue.</span>
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-pretty text-base leading-relaxed text-text-muted md:mt-4 md:text-lg" data-id="element-1009" data-mp="3icdbmyajesuw">
          Branded assets from your book that help you get in the room, continue
          the conversation, and move serious readers toward the right offer.
        </p>
      </div>

      {/* Desktop three-up → caption → controls */}
      <div className="mt-8 hidden lg:block md:mt-10" data-id="element-1010" data-mp="2zkucj2jmvyy5">
        <div className="mx-auto max-w-6xl" data-id="element-1011" data-mp="2zn6sja5tzz8k">
          <div className="flex items-center justify-center gap-4 overflow-x-clip xl:gap-6" data-id="element-1012" data-mp="34kgoz9rn1b5w">
            <button type="button" onClick={prev} aria-label={`Show previous: ${prevSlide.label}`} className="shrink-0 cursor-pointer opacity-75 transition-opacity duration-300 ease-out hover:opacity-90 focus:outline-none focus-visible:opacity-90 focus-visible:ring-2 focus-visible:ring-seaglass" data-id="element-1013" data-mp="eveepnlzz9z0">
              <motion.div key={`desk-prev-${prevSlide.id}`} initial={reduceMotion ? false : {
              opacity: 0.4,
              x: -12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={transition} data-id="element-1014" data-mp="3lcn1dvlqagf9">
                <AssetImage slide={prevSlide} className="se-card-side" data-id="element-1015" data-mp="3v887o5xty2ut" />
              </motion.div>
            </button>

            <div className="relative z-10 shrink-0" data-id="element-1016" data-mp="32lnhh8t3bwsu">
              <motion.div key={`desk-active-${slide.id}`} initial={reduceMotion ? false : {
              opacity: 0.55,
              scale: 0.97,
              x: 8
            }} animate={{
              opacity: 1,
              scale: 1,
              x: 0
            }} transition={transition} className="origin-center" data-id="element-1017" data-mp="32myhcxjf0keo">
                <AssetImage slide={slide} priority className="se-card-active" data-id="element-1018" data-mp="2os4i3kk3l44m" />
              </motion.div>
            </div>

            <button type="button" onClick={next} aria-label={`Show next: ${nextSlide.label}`} className="shrink-0 cursor-pointer opacity-75 transition-opacity duration-300 ease-out hover:opacity-90 focus:outline-none focus-visible:opacity-90 focus-visible:ring-2 focus-visible:ring-seaglass" data-id="element-1019" data-mp="zddeb7qm3hul">
              <motion.div key={`desk-next-${nextSlide.id}`} initial={reduceMotion ? false : {
              opacity: 0.4,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={transition} data-id="element-1020" data-mp="ue0hbu610uea">
                <AssetImage slide={nextSlide} className="se-card-side" data-id="element-1021" data-mp="1n261oft64hdl" />
              </motion.div>
            </button>
          </div>

          <div className="mt-6" data-id="element-1022" data-mp="1959firnzvo31">
            <ActiveSlideCaption slide={slide} reduceMotion={reduceMotion} transition={transition} data-id="element-1023" data-mp="1d3qm9a3i6wjx" />
          </div>

          <div className="mt-5" data-id="element-1024" data-mp="3tscjav2lmc8r">
            <Controls index={index} total={total} onPrev={prev} onNext={next} data-id="element-1025" data-mp="3tv8jkq1x5d0d" />
          </div>

          <p className="mt-4 text-center text-xs text-text-muted" data-id="element-1026" data-mp="1n8mb00p6bsj">
            Example from a client build — yours is branded to your book.
          </p>
        </div>
      </div>

      {/* Mobile: stage → caption → controls */}
      <div className="mt-7 lg:hidden" data-id="element-1027" data-mp="kbydj28se2hx">
        <div className="relative mx-auto flex max-w-md items-center justify-center overflow-x-clip px-1" data-id="element-1028" data-mp="kd1233imzsuo">
          <button type="button" onClick={prev} aria-label={`Show previous: ${prevSlide.label}`} className="absolute left-0 z-0 w-[20%] -translate-x-[18%] scale-95 cursor-pointer opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" data-id="element-1029" data-mp="fept0v061mdi">
            <AssetImage slide={prevSlide} className="w-full max-w-none" data-id="element-1030" data-mp="13akemh4aztlu" />
          </button>

          <div className="relative z-10 w-[76%]" data-id="element-1031" data-mp="yc9381yd1b1i">
            <motion.div key={`mob-active-${slide.id}`} initial={reduceMotion ? false : {
            opacity: 0.5,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={transition} data-id="element-1032" data-mp="ybhixna7qzqj">
              <AssetImage slide={slide} priority className="w-full max-w-none" data-id="element-1033" data-mp="1ybt5m4me4y0v" />
            </motion.div>
          </div>

          <button type="button" onClick={next} aria-label={`Show next: ${nextSlide.label}`} className="absolute right-0 z-0 w-[20%] translate-x-[18%] scale-95 cursor-pointer opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" data-id="element-1034" data-mp="23ae5g2xqdfos">
            <AssetImage slide={nextSlide} className="w-full max-w-none" data-id="element-1035" data-mp="2c9hqhcggk55x" />
          </button>
        </div>

        <div className="mt-5" data-id="element-1036" data-mp="2h8umont7yol6">
          <ActiveSlideCaption slide={slide} reduceMotion={reduceMotion} transition={transition} data-id="element-1037" data-mp="2m98j18lgka3d" />
        </div>

        <div className="mt-5" data-id="element-1038" data-mp="2r08h4h6bcy7g">
          <Controls index={index} total={total} onPrev={prev} onNext={next} data-id="element-1039" data-mp="2r1a51m8fhf6l" />
        </div>

        <p className="mt-4 text-center text-xs text-text-muted" data-id="element-1040" data-mp="2vz1w6e190x9q">
          Example from a client build — yours is branded to your book.
        </p>
      </div>
    </div>;
}